<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount, nextTick } from "vue";
import * as bootstrap from "bootstrap";
import { useAccountCategoryStore } from "@/stores/accountCategoryStore";

defineOptions({ name: "CategoriesModal" });

const categoryStore = useAccountCategoryStore();

/* Modal refs */
const mainModalEl = ref(null);
const deleteModalEl = ref(null);

let mainModalInstance = null;
let deleteModalInstance = null;

/* State */
const isEdit = ref(false);
const saving = ref(false);
const deletingId = ref(null);

/* Focus refs (UX) */
const nameInputEl = ref(null);

/* Form */
const form = reactive({
  id: null,
  name: "",
  sub_category_name: "",
  description: "",
  is_active: true,
});

/* UX validation */
const attempted = ref(false);
const errors = computed(() => {
  const e = {};
  if (!form.name || !String(form.name).trim()) e.name = "Category name is required.";
  if (form.name && String(form.name).trim().length < 2) e.name = "Use at least 2 characters.";
  return e;
});
const isValid = computed(() => Object.keys(errors.value).length === 0);

/* Helpers */
function resetForm() {
  form.id = null;
  form.name = "";
  form.sub_category_name = "";
  form.description = "";
  form.is_active = true;
  attempted.value = false;
}

function focusName() {
  nextTick(() => {
    nameInputEl.value?.focus?.();
  });
}

/* Submit */
async function submit() {
  attempted.value = true;
  if (!isValid.value) {
    focusName();
    return;
  }

  saving.value = true;

  const payload = {
    name: String(form.name).trim(),
    sub_category_name: form.sub_category_name ? String(form.sub_category_name).trim() : null,
    description: form.description ? String(form.description).trim() : null,
    is_active: !!form.is_active,
  };

  try {
    if (isEdit.value && form.id) {
      await categoryStore.update(form.id, payload);
    } else {
      await categoryStore.create(payload);
    }

    mainModalInstance?.hide();
    resetForm();
  } catch (e) {
    console.error("Failed to save account category", e);
  } finally {
    saving.value = false;
  }
}

/* Delete */
async function confirmDelete() {
  if (!deletingId.value) return;

  try {
    await categoryStore.remove(deletingId.value);
    deleteModalInstance?.hide();
    deletingId.value = null;
  } catch (e) {
    console.error("Failed to delete category", e);
  }
}

/* Exposed methods */
function openCreate() {
  isEdit.value = false;
  resetForm();
  mainModalInstance?.show();
  focusName();
}

function openEdit(row) {
  if (!row?.id) return;

  isEdit.value = true;
  resetForm();

  form.id = row.id;
  form.name = row.name || "";
  form.sub_category_name = row.sub_category_name || "";
  form.description = row.description || "";
  form.is_active = row.is_active ?? true;

  mainModalInstance?.show();
  focusName();
}

function openDelete(row) {
  deletingId.value = row?.id || null;
  deleteModalInstance?.show();
}

defineExpose({ openCreate, openEdit, openDelete });

/* Initialize Bootstrap modals (robust, no manual DOM class hacks) */
function onMainHidden() {
  resetForm();
}
function onDeleteHidden() {
  deletingId.value = null;
}

onMounted(() => {
  if (mainModalEl.value) {
    mainModalInstance = bootstrap.Modal.getOrCreateInstance(mainModalEl.value, {
      backdrop: "static",
      keyboard: true,
      focus: true,
    });

    mainModalEl.value.addEventListener("hidden.bs.modal", onMainHidden);
    mainModalEl.value.addEventListener("shown.bs.modal", focusName);
  }

  if (deleteModalEl.value) {
    deleteModalInstance = bootstrap.Modal.getOrCreateInstance(deleteModalEl.value, {
      backdrop: "static",
      keyboard: true,
      focus: true,
    });

    deleteModalEl.value.addEventListener("hidden.bs.modal", onDeleteHidden);
  }
});

onBeforeUnmount(() => {
  mainModalEl.value?.removeEventListener("hidden.bs.modal", onMainHidden);
  deleteModalEl.value?.removeEventListener("hidden.bs.modal", onDeleteHidden);
});
</script>

<template>
  <!-- ADD / EDIT CATEGORY -->
  <div
    class="modal fade dq-modal"
    id="category_modal"
    tabindex="-1"
    ref="mainModalEl"
    aria-hidden="true"
    :aria-labelledby="isEdit ? 'dqCatEditTitle' : 'dqCatAddTitle'"
  >
    <div class="modal-dialog modal-dialog-centered modal-md">
      <div class="modal-content dq-modal-card">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="d-flex align-items-center gap-3">
            <span class="dq-icon-badge" :class="isEdit ? 'dq-icon-badge-warn' : ''">
              <i :class="isEdit ? 'ti ti-edit' : 'ti ti-category-2'"></i>
            </span>

            <div class="lh-1">
              <h4
                class="modal-title mb-1"
                :id="isEdit ? 'dqCatEditTitle' : 'dqCatAddTitle'"
              >
                {{ isEdit ? "Edit Category" : "Add Category" }}
              </h4>
              <div class="dq-subtitle">
                {{ isEdit
                  ? "Update the category details. Changes apply immediately."
                  : "Create an account category to keep your finance workflows organized." }}
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn dq-close-btn"
            data-bs-dismiss="modal"
            aria-label="Close"
            :disabled="saving"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submit" novalidate>
          <!-- Body -->
          <div class="modal-body dq-modal-body">
            <div class="dq-form-grid">
              <!-- Category Name -->
              <div class="dq-field">
                <label class="form-label dq-label">
                  Category Name <span class="text-danger">*</span>
                </label>

                <div class="dq-input-wrap">
                  <span class="dq-input-icon">
                    <i class="ti ti-id"></i>
                  </span>
                  <input
                    ref="nameInputEl"
                    type="text"
                    class="form-control dq-input"
                    v-model="form.name"
                    placeholder="e.g. Travel, Taxes, Compensation"
                    autocomplete="off"
                    :aria-invalid="attempted && !!errors.name"
                  />
                </div>

                <div v-if="attempted && errors.name" class="dq-error">
                  <i class="ti ti-alert-circle me-1"></i>{{ errors.name }}
                </div>
                <div v-else class="dq-help">
                  Use a recognizable label. Keep naming consistent to avoid duplicates.
                </div>
              </div>

              <!-- Sub category -->
              <div class="dq-field">
                <label class="form-label dq-label">Sub Category Name</label>

                <div class="dq-input-wrap">
                  <span class="dq-input-icon">
                    <i class="ti ti-sitemap"></i>
                  </span>
                  <input
                    type="text"
                    class="form-control dq-input"
                    v-model="form.sub_category_name"
                    placeholder="e.g. Business Travel, Payroll Taxes"
                    autocomplete="off"
                  />
                </div>

                <div class="dq-help">
                  Optional. Use this to group categories into clearer reporting buckets.
                </div>
              </div>

              <!-- Description -->
              <div class="dq-field">
                <label class="form-label dq-label">Description</label>

                <div class="dq-textarea-wrap">
                  <textarea
                    rows="3"
                    class="form-control dq-textarea"
                    v-model="form.description"
                    placeholder="Optional description for this category"
                  ></textarea>
                </div>

                <div class="dq-help">
                  Add a short note for admins (e.g., what transactions belong here).
                </div>
              </div>

              <!-- Active -->
              <div class="dq-field">
                <div class="dq-switch-row">
                  <div class="dq-switch-text">
                    <div class="dq-switch-title">Active</div>
                    <div class="dq-switch-sub">
                      Inactive categories won’t appear in new dropdown selections.
                    </div>
                  </div>

                  <label class="dq-switch">
                    <input type="checkbox" v-model="form.is_active" />
                    <span class="dq-slider"></span>
                  </label>
                </div>

                <div class="dq-help">
                  <span class="dq-pill dq-pill-success" v-if="form.is_active">Active</span>
                  <span class="dq-pill dq-pill-muted" v-else>Inactive</span>
                  <span class="ms-2 text-muted">You can change this anytime.</span>
                </div>
              </div>

              <!-- Tip card -->
              <div class="dq-tip">
                <div class="dq-tip-icon">
                  <i class="ti ti-bulb"></i>
                </div>
                <div>
                  <div class="dq-tip-title">Quick tip</div>
                  <div class="dq-tip-text">
                    Keep your categories aligned with your Chart of Accounts so reports stay clean.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer (sticky) -->
          <div class="modal-footer dq-modal-footer">
            <button
              type="button"
              class="btn btn-light dq-btn-soft me-2"
              data-bs-dismiss="modal"
              :disabled="saving"
            >
              Cancel
            </button>

            <button
              type="submit"
              class="btn btn-primary dq-btn-primary"
              :disabled="saving || (attempted && !isValid)"
            >
              <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
              <span v-if="saving" aria-live="polite">{{ isEdit ? "Saving…" : "Adding…" }}</span>
              <span v-else>{{ isEdit ? "Save Changes" : "Add Category" }}</span>
              <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- DELETE MODAL -->
  <div
    class="modal fade dq-modal"
    id="delete_modal_category"
    tabindex="-1"
    ref="deleteModalEl"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-sm">
      <div class="modal-content dq-modal-card">
        <div class="modal-body dq-delete-body">
          <div class="dq-danger-hero">
            <span class="dq-danger-badge">
              <i class="ti ti-trash-x"></i>
            </span>
          </div>

          <h4 class="mb-1 dq-delete-title">Confirm delete</h4>
          <p class="mb-3 dq-delete-subtitle">
            This action permanently removes the category. You can’t undo this.
          </p>

          <div class="dq-delete-warning">
            <i class="ti ti-alert-triangle me-2"></i>
            Tip: Consider setting the category to <strong>Inactive</strong> if it’s used in reports.
          </div>

          <div class="d-flex justify-content-center gap-2 mt-4">
            <button
              type="button"
              class="btn btn-light dq-btn-soft"
              data-bs-dismiss="modal"
              :disabled="categoryStore.loadingForDelete"
            >
              Cancel
            </button>

            <button
              type="button"
              class="btn btn-danger dq-btn-danger"
              @click="confirmDelete"
              :disabled="categoryStore.loadingForDelete"
            >
              <span
                v-if="categoryStore.loadingForDelete"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              <span v-if="categoryStore.loadingForDelete">Deleting…</span>
              <span v-else>Yes, Delete</span>
              <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ===== Premium modal system (matches your department modal style) ===== */
.dq-modal-card {
  border: 0;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 22px 60px rgba(16, 24, 40, 0.18);
}

.dq-modal-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  padding: 18px 18px;
  background: linear-gradient(180deg, rgba(248, 250, 252, 1), rgba(255, 255, 255, 1));
}

.dq-subtitle {
  font-size: 0.85rem;
  color: rgba(17, 24, 39, 0.6);
  margin-top: 2px;
}

.dq-close-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}
.dq-close-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(16, 24, 40, 0.08);
}

.dq-icon-badge {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(59, 130, 246, 0.12);
  color: rgba(37, 99, 235, 1);
  font-size: 20px;
}
.dq-icon-badge-warn {
  background: rgba(245, 158, 11, 0.14);
  color: rgba(217, 119, 6, 1);
}

.dq-modal-body {
  padding: 18px;
  max-height: 70vh;
  overflow: auto;
}

.dq-form-grid {
  display: grid;
  gap: 14px;
}

.dq-label {
  font-weight: 800;
  color: rgba(17, 24, 39, 0.9);
  margin-bottom: 6px;
}

.dq-input-wrap,
.dq-textarea-wrap {
  position: relative;
}

.dq-input-icon {
  position: absolute;
  left: 12px;
  top: 12px;
  color: rgba(17, 24, 39, 0.45);
  z-index: 3;
  pointer-events: none;
}

.dq-input {
  padding-left: 40px !important;
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  min-height: 44px;
  transition: box-shadow 0.12s ease, border-color 0.12s ease;
}
.dq-input:focus {
  border-color: rgba(59, 130, 246, 0.45) !important;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.12) !important;
}

.dq-textarea {
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  transition: box-shadow 0.12s ease, border-color 0.12s ease;
}
.dq-textarea:focus {
  border-color: rgba(59, 130, 246, 0.45) !important;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.10) !important;
}

.dq-help {
  font-size: 0.82rem;
  color: rgba(17, 24, 39, 0.6);
  margin-top: 6px;
}

.dq-error {
  margin-top: 6px;
  font-size: 0.82rem;
  font-weight: 700;
  color: rgba(220, 38, 38, 1);
  display: inline-flex;
  align-items: center;
}

.dq-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  font-weight: 800;
  font-size: 0.75rem;
}
.dq-pill-success {
  background: rgba(16, 185, 129, 0.12);
  color: rgba(5, 150, 105, 1);
}
.dq-pill-muted {
  background: rgba(148, 163, 184, 0.18);
  color: rgba(71, 85, 105, 1);
}

/* Tip card */
.dq-tip {
  display: flex;
  gap: 12px;
  padding: 12px;
  border-radius: 16px;
  background: rgba(99, 102, 241, 0.08);
  border: 1px solid rgba(99, 102, 241, 0.12);
}
.dq-tip-icon {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(99, 102, 241, 0.14);
  color: rgba(79, 70, 229, 1);
}
.dq-tip-title {
  font-weight: 900;
  color: rgba(17, 24, 39, 0.9);
  margin-bottom: 2px;
}
.dq-tip-text {
  font-size: 0.83rem;
  color: rgba(17, 24, 39, 0.6);
}

/* Sticky footer */
.dq-modal-footer {
  position: sticky;
  bottom: 0;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  padding: 14px 18px;
}

.dq-btn-soft {
  border-radius: 12px;
  padding: 10px 14px;
}
.dq-btn-primary {
  border-radius: 12px;
  padding: 10px 14px;
  box-shadow: 0 14px 26px rgba(37, 99, 235, 0.18);
}

/* Switch */
.dq-switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(248, 250, 252, 0.8);
}
.dq-switch-text {
  min-width: 0;
}
.dq-switch-title {
  font-weight: 900;
  color: rgba(17, 24, 39, 0.9);
}
.dq-switch-sub {
  font-size: 0.82rem;
  color: rgba(17, 24, 39, 0.6);
  margin-top: 2px;
}

/* iOS-like switch */
.dq-switch {
  position: relative;
  width: 46px;
  height: 26px;
  display: inline-block;
  flex: 0 0 auto;
}
.dq-switch input {
  display: none;
}
.dq-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: rgba(148, 163, 184, 0.55);
  border-radius: 999px;
  transition: 0.18s ease;
}
.dq-slider:before {
  content: "";
  position: absolute;
  height: 20px;
  width: 20px;
  left: 3px;
  top: 3px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 8px 18px rgba(16, 24, 40, 0.18);
  transition: 0.18s ease;
}
.dq-switch input:checked + .dq-slider {
  background: rgba(37, 99, 235, 0.75);
}
.dq-switch input:checked + .dq-slider:before {
  transform: translateX(20px);
}

/* Delete modal */
.dq-delete-body {
  padding: 18px;
  text-align: center;
}
.dq-danger-hero {
  display: flex;
  justify-content: center;
  margin-top: 6px;
}
.dq-danger-badge {
  width: 64px;
  height: 64px;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(239, 68, 68, 0.12);
  color: rgba(220, 38, 38, 1);
  font-size: 26px;
}
.dq-delete-title {
  font-weight: 950;
  letter-spacing: -0.02em;
}
.dq-delete-subtitle {
  color: rgba(17, 24, 39, 0.65);
  font-size: 0.92rem;
  line-height: 1.35rem;
}
.dq-delete-warning {
  text-align: left;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(245, 158, 11, 0.10);
  border: 1px solid rgba(245, 158, 11, 0.14);
  color: rgba(120, 53, 15, 0.95);
  font-size: 0.84rem;
}
.dq-btn-danger {
  border-radius: 12px;
  padding: 10px 14px;
  box-shadow: 0 14px 26px rgba(220, 38, 38, 0.14);
}
</style>
